import { achievements, type GameState } from '@/engine/gameEngine'
import type { ExerciseAttempt } from '@/entities/attempt'

const EVIDENCE_GOALS = [
  { id: 'first-word', target: 1 },
  { id: 'word-collector', target: 10 },
] as const

export interface EvidenceAchievementGoal {
  id: (typeof EVIDENCE_GOALS)[number]['id']
  current: number
  target: number
}

function isLearnedVocabularyEvidence(attempt: ExerciseAttempt): boolean {
  return attempt.correct
    && attempt.scoringMethod !== 'not-observed'
    && attempt.category === 'vocabulary'
    && Boolean(attempt.knowledgePointId)
}

export function getEvidenceAchievementProgress(attempts: readonly ExerciseAttempt[]) {
  const learnedVocabularyCount = new Set(
    attempts
      .filter(isLearnedVocabularyEvidence)
      .map((attempt) => attempt.knowledgePointId as string),
  ).size

  return {
    learnedVocabularyCount,
    goals: EVIDENCE_GOALS.map((goal): EvidenceAchievementGoal => ({
      ...goal,
      current: Math.min(learnedVocabularyCount, goal.target),
    })),
  }
}

export function awardEvidenceAchievements(
  game: GameState,
  attempts: readonly ExerciseAttempt[],
): { game: GameState; unlockedIds: string[] } {
  const progress = getEvidenceAchievementProgress(attempts)
  const unlockedIds = progress.goals
    .filter((goal) => goal.current >= goal.target && !game.achievements.includes(goal.id))
    .map((goal) => goal.id)

  if (unlockedIds.length === 0) return { game, unlockedIds }

  const rewardById = new Map(achievements.map((achievement) => [achievement.id, achievement.reward]))
  const reward = unlockedIds.reduce((sum, id) => sum + (rewardById.get(id) ?? 0), 0)
  return {
    game: {
      ...game,
      xp: game.xp + reward,
      achievements: [...game.achievements, ...unlockedIds],
    },
    unlockedIds,
  }
}

